import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, DollarSign, FileText, CheckCircle, AlertCircle, Download, Mail, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { facturacionAPI } from '../services/api';

function AdminFacturacion() {
  const [historico, setHistorico] = useState([]);
  const [detalle, setDetalle] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [mesSeleccionado, setMesSeleccionado] = useState(() => {
    const now = new Date();
    return { mes: now.getMonth() + 1, anio: now.getFullYear() };
  });
  const [descargando, setDescargando] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const navigate = useNavigate();
  const { usuario, loading: authLoading } = useAuth();

  useEffect(() => {
    if (authLoading) return;
    if (usuario?.rol !== 'admin') {
      navigate('/');
      return;
    }
    cargarHistorico();
  }, [authLoading, usuario]);

  useEffect(() => {
    cargarDetalle();
  }, [mesSeleccionado]);

  const cargarHistorico = async () => {
    try {
      const response = await facturacionAPI.historico();
      setHistorico(response.data.data || []);
    } catch (error) {
      console.error('Error cargando histórico:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarDetalle = async () => {
    setDetalleLoading(true);
    try {
      const response = await facturacionAPI.detalleMes(mesSeleccionado.mes, mesSeleccionado.anio);
      setDetalle(response.data.data || []);
    } catch (error) {
      console.error('Error cargando detalle:', error);
      setDetalle([]);
    } finally {
      setDetalleLoading(false);
    }
  };

  const handleDescargarExcel = async () => {
    setDescargando(true);
    try {
      const response = await facturacionAPI.descargarExcel(mesSeleccionado.mes, mesSeleccionado.anio);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte-multas-${mesSeleccionado.anio}-${String(mesSeleccionado.mes).padStart(2, '0')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error descargando Excel:', error);
      alert('Error descargando Excel');
    } finally {
      setDescargando(false);
    }
  };

  const handleEnviarEmail = async () => {
    if (!window.confirm(`¿Enviar reporte de ${getNombreMes(mesSeleccionado.mes)} ${mesSeleccionado.anio} por email?`)) return;
    setEnviando(true);
    try {
      await facturacionAPI.enviarEmail(mesSeleccionado.mes, mesSeleccionado.anio);
      alert('Reporte enviado correctamente');
    } catch (error) {
      console.error('Error enviando reporte:', error);
      alert('Error enviando reporte');
    } finally {
      setEnviando(false);
    }
  };

  const cambiarMes = (direccion) => {
    setMesSeleccionado(prev => {
      let mes = prev.mes + direccion;
      let anio = prev.anio;
      if (mes > 12) { mes = 1; anio++; }
      if (mes < 1) { mes = 12; anio--; }
      return { mes, anio };
    });
  };

  // Datos para el resumen del mes seleccionado
  const resumenMes = useMemo(() => {
    const totalMultas = detalle.reduce((sum, e) => sum + e.total_multas, 0);
    const totalAutomaticas = detalle.reduce((sum, e) => sum + e.automaticas, 0);
    const totalManuales = detalle.reduce((sum, e) => sum + e.manuales, 0);
    const totalFacturado = detalle.reduce((sum, e) => sum + parseFloat(e.total), 0);
    return { totalMultas, totalAutomaticas, totalManuales, totalFacturado };
  }, [detalle]);

  // Datos para la gráfica (últimos 12 meses, cronológico)
  const chartData = useMemo(() => {
    const sorted = [...historico].sort((a, b) => new Date(a.periodo) - new Date(b.periodo));
    const maxTotal = Math.max(...sorted.map(h => parseFloat(h.total_facturado)), 1);
    return { meses: sorted, maxTotal };
  }, [historico]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg shadow-soft border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-display">Facturacion</h1>
              <p className="text-sm text-slate-600">Historico y detalle mensual</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats del mes seleccionado */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard icon={DollarSign} title="Total Facturado" value={`${resumenMes.totalFacturado.toFixed(2)}€`} subtitle={`${getNombreMes(mesSeleccionado.mes)} ${mesSeleccionado.anio}`} color="violet" />
          <StatCard icon={FileText} title="Total Multas" value={resumenMes.totalMultas} subtitle="Comunicadas" color="primary" />
          <StatCard icon={CheckCircle} title="Automaticas" value={`${resumenMes.totalAutomaticas} (${(resumenMes.totalAutomaticas * 4).toFixed(0)}€)`} subtitle="4€ / multa" color="emerald" />
          <StatCard icon={AlertCircle} title="Manuales" value={`${resumenMes.totalManuales} (${(resumenMes.totalManuales * 6).toFixed(0)}€)`} subtitle="6€ / multa" color="amber" />
        </div>

        {/* Gráfica de barras */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-soft p-6 mb-8">
          <h2 className="text-xl font-bold text-slate-900 font-display mb-6">Facturacion Mensual</h2>
          {chartData.meses.length === 0 ? (
            <p className="text-slate-500 text-center py-12">No hay datos de facturacion</p>
          ) : (
            <div className="flex items-end gap-2 h-64">
              {chartData.meses.map((mes) => {
                const autoTotal = mes.automaticas * 4;
                const manualTotal = mes.manuales * 6;
                const total = parseFloat(mes.total_facturado);
                const autoHeight = (autoTotal / chartData.maxTotal) * 100;
                const manualHeight = (manualTotal / chartData.maxTotal) * 100;
                const mesDate = new Date(mes.periodo);
                const mesLabel = mesDate.toLocaleDateString('es-ES', { month: 'short' }).replace('.', '');
                const anioLabel = mesDate.getFullYear();
                const isSelected = (mesDate.getMonth() + 1) === mesSeleccionado.mes && mesDate.getFullYear() === mesSeleccionado.anio;

                return (
                  <div
                    key={mes.periodo}
                    className={`flex-1 flex flex-col items-center cursor-pointer group ${isSelected ? 'opacity-100' : 'opacity-75 hover:opacity-100'}`}
                    onClick={() => setMesSeleccionado({ mes: mesDate.getMonth() + 1, anio: mesDate.getFullYear() })}
                  >
                    {/* Tooltip */}
                    <div className="hidden group-hover:block absolute -mt-8 bg-slate-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                      {total.toFixed(0)}€
                    </div>
                    {/* Barras apiladas */}
                    <div className="w-full flex flex-col items-center justify-end h-48 relative">
                      <div className="w-full max-w-12 flex flex-col justify-end" style={{ height: '100%' }}>
                        {manualHeight > 0 && (
                          <div
                            className={`w-full rounded-t ${isSelected ? 'bg-amber-500' : 'bg-amber-400'} transition-all`}
                            style={{ height: `${manualHeight}%`, minHeight: manualHeight > 0 ? '2px' : '0' }}
                          />
                        )}
                        {autoHeight > 0 && (
                          <div
                            className={`w-full ${manualHeight === 0 ? 'rounded-t' : ''} ${isSelected ? 'bg-emerald-500' : 'bg-emerald-400'} transition-all`}
                            style={{ height: `${autoHeight}%`, minHeight: autoHeight > 0 ? '2px' : '0' }}
                          />
                        )}
                      </div>
                    </div>
                    {/* Etiqueta */}
                    <div className={`text-center mt-2 ${isSelected ? 'font-bold' : ''}`}>
                      <div className={`text-xs capitalize ${isSelected ? 'text-primary-600' : 'text-slate-600'}`}>{mesLabel}</div>
                      <div className="text-xs text-slate-400">{anioLabel}</div>
                    </div>
                    {/* Indicador de selección */}
                    {isSelected && <div className="w-2 h-2 rounded-full bg-primary-500 mt-1" />}
                  </div>
                );
              })}
            </div>
          )}
          {/* Leyenda */}
          <div className="flex items-center gap-6 mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-emerald-500" />
              <span className="text-sm text-slate-600">Automaticas (4€)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-amber-500" />
              <span className="text-sm text-slate-600">Manuales (6€)</span>
            </div>
          </div>
        </div>

        {/* Tabla detalle mensual */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-soft">
          {/* Header de la tabla */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h2 className="text-xl font-bold text-slate-900 font-display">Detalle por Empresa</h2>
                {/* Selector de mes */}
                <div className="flex items-center gap-2 bg-slate-100 rounded-xl px-2 py-1">
                  <button onClick={() => cambiarMes(-1)} className="p-1 hover:bg-slate-200 rounded-lg transition-colors">
                    <ChevronLeft className="w-4 h-4 text-slate-600" />
                  </button>
                  <span className="text-sm font-medium text-slate-700 min-w-[140px] text-center">
                    {getNombreMes(mesSeleccionado.mes)} {mesSeleccionado.anio}
                  </span>
                  <button onClick={() => cambiarMes(1)} className="p-1 hover:bg-slate-200 rounded-lg transition-colors">
                    <ChevronRight className="w-4 h-4 text-slate-600" />
                  </button>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleDescargarExcel}
                  disabled={descargando}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-50 transition-all text-sm"
                >
                  <Download className="w-4 h-4" />
                  {descargando ? 'Descargando...' : 'Excel'}
                </button>
                <button
                  onClick={handleEnviarEmail}
                  disabled={enviando}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 disabled:opacity-50 transition-all text-sm"
                >
                  <Mail className="w-4 h-4" />
                  {enviando ? 'Enviando...' : 'Email'}
                </button>
              </div>
            </div>
          </div>

          {/* Tabla */}
          <div className="overflow-x-auto">
            {detalleLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : detalle.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                No hay datos de facturacion para {getNombreMes(mesSeleccionado.mes)} {mesSeleccionado.anio}
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left py-3 px-6 font-medium text-slate-700">Empresa</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-700">CIF</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-700">Total Multas</th>
                    <th className="text-center py-3 px-4 font-medium text-emerald-700">Automaticas</th>
                    <th className="text-center py-3 px-4 font-medium text-amber-700">Manuales</th>
                    <th className="text-right py-3 px-6 font-medium text-violet-700">Total €</th>
                  </tr>
                </thead>
                <tbody>
                  {detalle.map((empresa) => (
                    <tr key={empresa.empresa_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-6 font-medium text-slate-900">{empresa.nombre}</td>
                      <td className="py-3 px-4 text-center text-slate-600 text-sm">{empresa.cif}</td>
                      <td className="py-3 px-4 text-center text-slate-900 font-medium">{empresa.total_multas}</td>
                      <td className="py-3 px-4 text-center text-emerald-600 font-medium">{empresa.automaticas}</td>
                      <td className="py-3 px-4 text-center text-amber-600 font-medium">{empresa.manuales}</td>
                      <td className="py-3 px-6 text-right font-bold text-slate-900">{parseFloat(empresa.total).toFixed(2)}€</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 border-t-2 border-slate-300">
                    <td className="py-3 px-6 font-bold text-slate-900">TOTAL</td>
                    <td className="py-3 px-4"></td>
                    <td className="py-3 px-4 text-center font-bold text-slate-900">{resumenMes.totalMultas}</td>
                    <td className="py-3 px-4 text-center font-bold text-emerald-600">{resumenMes.totalAutomaticas}</td>
                    <td className="py-3 px-4 text-center font-bold text-amber-600">{resumenMes.totalManuales}</td>
                    <td className="py-3 px-6 text-right font-bold text-violet-700 text-lg">{resumenMes.totalFacturado.toFixed(2)}€</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, title, value, subtitle, color }) {
  const colors = {
    primary: 'bg-primary-100 text-primary-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600',
    violet: 'bg-violet-100 text-violet-600',
  };
  return (
    <div className="stat-card border border-slate-200">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl ${colors[color]}`}><Icon className="w-6 h-6" /></div>
      </div>
      <h3 className="text-slate-600 text-sm font-medium mb-1">{title}</h3>
      <p className="text-3xl font-bold text-slate-900 mb-1">{value}</p>
      <p className="text-sm text-slate-500">{subtitle}</p>
    </div>
  );
}

function getNombreMes(mes) {
  const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  return meses[mes - 1] || '';
}

export default AdminFacturacion;
